import { AmplitudePageName, getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import {
    getSearchSellerItemsSearch,
    getSearchSellerItemsSearchSellerId,
} from '@/redux/modules/search/search.selectors';
import { getSellerName } from '@/redux/modules/seller';
import { getUrlFromPathname } from '@/redux/modules/config';
import { parseQueryParams } from '@/utils/queryParams';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '../core.types';
import { useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export enum AuctionType {
    BuyNow = 'Buy Now',
    Live = 'Live',
    PreviewOnly = 'Preview Only',
    Timed = 'Timed',
    TimedPlus = 'Timed+',
}

export enum ItemCardNavigationTrigger {
    BidLiveClick = 'Bid Live Click',
    CountdownClick = 'Countdown Click',
    HouseNameClick = 'House Name Click',
    ImageClick = 'Image Click',
    PriceClick = 'Price Click',
    TitleClick = 'Title Click',
}

export type TrackItemCardNavigationPayload = TrackingPayload & {
    auctionType?: AuctionType;
    catalogId: number;
    featured: boolean;
    houseId: number;
    itemId: number;
    normalizedSearchQuery?: string;
    position?: number;
    trigger: ItemCardNavigationTrigger;
};

export type TrackItemCardNavigationCallbackParams = Omit<
    TrackItemCardNavigationPayload,
    'pageName' | 'url' | 'normalizedSearchQuery'
>;

export const trackItemCardNavigationAnalytics = (payload: TrackItemCardNavigationPayload) => {
    track(TrackingEvents.Item.ItemCardClicked, payload);
};

export const useTrackItemCardNavigationAnalytics = () => {
    const { pathname, search } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));
    const sellerItemsSearch = useAppSelector(getSearchSellerItemsSearch);
    const sellerId = useAppSelector(getSearchSellerItemsSearchSellerId);
    const sellerName = useAppSelector((state) => getSellerName(state, sellerId));

    return useCallback(
        (params: TrackItemCardNavigationCallbackParams) => {
            const { auctionType, catalogId, featured, houseId, itemId, position, trigger } = params;
            const pageName = getAmplitudePageNameFromPath(pathname, search);

            const basePayload: TrackItemCardNavigationPayload = {
                auctionType,
                catalogId,
                featured,
                houseId,
                itemId,
                pageName,
                position,
                trigger,
                url,
            };

            if (pageName === AmplitudePageName.SearchResults) {
                const keyword = parseQueryParams(search).keyword ?? '';
                const fromSellerPage = sellerItemsSearch && !keyword;
                const normalizedSearchQuery = (fromSellerPage ? sellerName : keyword)?.toLowerCase() ?? '';

                return trackItemCardNavigationAnalytics({
                    ...basePayload,
                    normalizedSearchQuery,
                });
            }

            return trackItemCardNavigationAnalytics(basePayload);
        },
        [pathname, search, url, sellerItemsSearch, sellerName]
    );
};
